import { Col, Container, Row, Spinner, Tab } from 'react-bootstrap'
import { Tabs } from '../../components/Tabs/Tabs'

import React, { useMemo, useState } from 'react'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetBiDashboardsQuery } from '../../store/insights/insights'
import { useGetRetentionPoliciesQuery } from '../../store/account/account'
import { RetentionResourceType, AuditLogRetention } from '../../models/RetentionPolicy'
import DashboardView from './DashboardView'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import './Analytics.scss'
import { NoQuotaModal } from './NoQuotaModal'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { AuditLogDisabledBox } from './AuditLogDisabledBox'
import { AuditLogSearchInfoBox } from './AuditLogSearchInfoBox'
import type { Dashboard } from '../../store/insights/insights'

const isAuditLogDashboard = (dashboard: Dashboard): boolean => dashboard.id === 'audit_log' || dashboard.name.toLowerCase().includes('audit log')

export const Analytics = () => {
  useDocumentTitle('Analytics')
  const accountId = useAppSelector(selectAccountId)
  const { hasQuota, isFetching: quotasIsFetching } = useZocksQuota()
  const dashboardAvailable = hasQuota('insights.enabled.value')
  const { data: dashboards, isFetching } = useGetBiDashboardsQuery({ accountId }, { skip: !dashboardAvailable })
  const { data: retentionPoliciesData, isFetching: isRetentionPoliciesFetching } = useGetRetentionPoliciesQuery({ accountId }, { skip: !dashboardAvailable })
  const [activeTabKey, setActiveTabKey] = useState<string | undefined>()

  const auditLogEnabled = useMemo(() => {
    if (isRetentionPoliciesFetching || !retentionPoliciesData) {
      return false
    }
    const searchableLogPolicy = retentionPoliciesData.data?.find(
      (policy) => policy.resource.type === RetentionResourceType.AUDIT_LOG && (policy.resource as AuditLogRetention).config?.kind === 'searchable_log',
    )
    if (!searchableLogPolicy) {
      return false
    }
    const defaultEnabled = (searchableLogPolicy.resource as AuditLogRetention).config?.defaultEnabled
    return defaultEnabled !== false
  }, [retentionPoliciesData, isRetentionPoliciesFetching])

  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  const accountDashboardAvailable = () => {
    return !quotasIsFetching && dashboardAvailable
  }

  return (
    <Container className='d-flex flex-column zocks-gap-1.25 p-0 w-100 mw-100 full-size-content'>
      {(isFetching || quotasIsFetching || (dashboardAvailable && isRetentionPoliciesFetching)) && (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )}

      {!quotasIsFetching && !(dashboardAvailable && isRetentionPoliciesFetching) && (
        <ScopedBlock authActions={[accountDashboardAvailable]} missingScopeAction={MissingScopeAction.MODAL} modal={<NoQuotaModal />}>
          {dashboards && dashboards.length > 0 && (
            <Tabs activeKey={activeTabKey} id='insightsTab' className='d-flex mb-3' onSelect={changeTab} mountOnEnter>
              {dashboards.map((dashboard) => {
                const showAuditLogDisabledBox = isAuditLogDashboard(dashboard) && !auditLogEnabled

                return (
                  <Tab key={dashboard.id} eventKey={dashboard.id} title={dashboard.name}>
                    <div>
                      {showAuditLogDisabledBox ? (
                        <AuditLogDisabledBox />
                      ) : (
                        <>
                          {isAuditLogDashboard(dashboard) && <AuditLogSearchInfoBox />}
                          <DashboardView dashboard={dashboard} initialHeight={1300} urlParams={{ accountId }}></DashboardView>
                        </>
                      )}
                    </div>
                  </Tab>
                )
              })}
            </Tabs>
          )}
        </ScopedBlock>
      )}
      {!quotasIsFetching && !dashboardAvailable && (
        <Row>
          <Col>
            <img alt='' src='https://static.zocks.io/assets/images/analytics hub_bg_2540_1429.png' className='w-100' />
          </Col>
        </Row>
      )}
    </Container>
  )
}
